import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Instagram, Youtube, Music2, Volume2, VolumeX, ChevronLeft, ChevronRight } from "lucide-react";
import { PORTFOLIO_VIDEOS, ICLOSED_URL } from "../data/content";
import { Reveal, Overline } from "./Reveal";

const PlatformIcon = ({ platform, size = 13 }) => {
  if (platform === "TikTok") return <Music2 size={size} />;
  if (platform === "Shorts") return <Youtube size={size} />;
  return <Instagram size={size} />;
};

// 3× render for a seamless "360°" infinite carousel: user always sees the middle
// copy ; if scroll drifts into first or third copy we silently reset scrollLeft to
// the equivalent position in the middle copy.
const COPIES = 3;

export default function Portfolio() {
  // On very narrow screens (phones), drop 2 heavier / repetitive videos to keep
  // network + decoding load minimal. The circular layout stays visually balanced.
  const IS_MOBILE = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  const items = IS_MOBILE
    ? PORTFOLIO_VIDEOS.filter((v) => {
        // Remove Roméo ads + the second Alex Hitchens (the one with black bars).
        if (v.client === "Roméo") return false;
        if (v.src && v.src.includes("thefrenchitch")) return false;
        // Mobile perf: keep only 2 Podmax videos instead of 3 to reduce
        // simultaneous video decoding load (iOS Safari struggles with 12+).
        if (v.src && v.src.includes("b727ca99-8e25-4a4d-bdda-ab87ee82b1ee")) return false;
        return true;
      })
    : PORTFOLIO_VIDEOS;
  const N = items.length;
  // On mobile we render a single copy (12 cards) instead of 3 (36 cards).
  // This is a massive DOM + memory win — no seamless loop, but users typically
  // don't scroll a portfolio carousel end-to-end on a phone.
  const RENDER_COPIES = IS_MOBILE ? 1 : COPIES;
  const displayed = useMemo(
    () => Array.from({ length: RENDER_COPIES }, () => items).flat(),
    [items, RENDER_COPIES]
  );

  const scrollerRef = useRef(null);
  const videoRefs = useRef([]);
  const isResetting = useRef(false);
  const [sound, setSound] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [active, setActive] = useState(0); // active in [0, N)
  // Cards whose <video> src has been attached (lazy loading via IntersectionObserver).
  // Only videos inside/near viewport actually download bytes — massive mobile win.
  // Pre-populate with just the first 5 cards of the middle copy (visible at init
  // on any screen). Others load on-demand via the IntersectionObserver below.
  const [loadedCards, setLoadedCards] = useState(() => {
    // Preload the very first cards synchronously so first paint has real videos.
    // On mobile (1 copy = N cards): preload ALL — iOS Safari won't autoplay a
    // <video> that has no src attached; metadata preload gives us a poster frame
    // even for cards not currently in view.
    // On desktop (3 copies): preload 5 cards from the middle copy.
    const s = new Set();
    const initialCount = IS_MOBILE ? N * RENDER_COPIES : 5;
    for (let k = 0; k < initialCount; k++) s.add(k);
    return s;
  });

  // On desktop, also preload the middle-copy starting cards (used for seamless loop init).
  useEffect(() => {
    if (RENDER_COPIES <= 1) return;
    setLoadedCards((prev) => {
      const next = new Set(prev);
      for (let k = N; k < N + 5; k++) next.add(k);
      return next;
    });
  }, [N, RENDER_COPIES]);

  const toggleSound = (i) => {
    const v = videoRefs.current[i];
    if (!v) return;
    if (sound === i) {
      v.muted = true;
      setSound(null);
    } else {
      videoRefs.current.forEach((el, j) => {
        if (el && j !== i) el.muted = true;
      });
      v.muted = false;
      v.play().catch(() => {});
      setSound(i);
    }
  };

  // Centered card in the DOM (0-based across all 3 copies)
  const getCenteredIndex = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return 0;
    const cards = el.querySelectorAll("[data-portfolio-card]");
    if (!cards.length) return 0;
    const scrollerCenter = el.scrollLeft + el.clientWidth / 2;
    let bestIndex = 0;
    let bestDelta = Infinity;
    cards.forEach((c, i) => {
      const cardCenter = c.offsetLeft + c.clientWidth / 2;
      const d = Math.abs(cardCenter - scrollerCenter);
      if (d < bestDelta) {
        bestDelta = d;
        bestIndex = i;
      }
    });
    return bestIndex;
  }, []);

  // Silently reset scroll into the middle copy when we drift into first / third copy.
  const seamlessReset = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-portfolio-card]");
    if (cards.length < N * RENDER_COPIES) return;
    const cx = getCenteredIndex();
    let targetIdx = null;
    if (cx < N) targetIdx = cx + N; // jump forward into middle copy
    else if (cx >= N * 2) targetIdx = cx - N; // jump backward into middle copy
    if (targetIdx == null) return;
    const target = cards[targetIdx];
    if (!target) return;
    isResetting.current = true;
    const prev = el.style.scrollBehavior;
    el.style.scrollBehavior = "auto";
    el.scrollLeft = target.offsetLeft - (el.clientWidth - target.clientWidth) / 2;
    // Restore scroll-behavior on next frame so the smooth animations of button-clicks keep working.
    requestAnimationFrame(() => {
      el.style.scrollBehavior = prev || "";
      isResetting.current = false;
    });
  }, [N, getCenteredIndex]);

  const updateActive = useCallback(() => {
    if (isResetting.current) return;
    const cx = getCenteredIndex();
    setActive(((cx % N) + N) % N);
  }, [N, getCenteredIndex]);

  // Init: center scroll on the middle copy (or first card on mobile with 1 copy).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-portfolio-card]");
    if (cards.length < N) return;
    const startIndex = RENDER_COPIES > 1 ? N : 0;
    const midStart = cards[startIndex];
    if (midStart) {
      el.style.scrollBehavior = "auto";
      el.scrollLeft = midStart.offsetLeft - (el.clientWidth - midStart.clientWidth) / 2;
      requestAnimationFrame(() => {
        el.style.scrollBehavior = "smooth";
      });
    }
    setActive(0);
  }, [N, RENDER_COPIES]);

  // Attach scroll listeners.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let idleTimer = null;
    const onScroll = () => {
      updateActive();
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(seamlessReset, 140);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateActive);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateActive);
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [updateActive, seamlessReset]);

  // Lazy-load video sources when cards enter (or approach) the viewport.
  // Uses a horizontal rootMargin so we begin loading a card ~1 card-width before
  // it becomes visible — the video is ready by the time it slides into view.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const cards = scroller.querySelectorAll("[data-portfolio-card]");
    if (!cards.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        setLoadedCards((prev) => {
          let next = prev;
          for (const e of entries) {
            if (e.isIntersecting) {
              const idx = Number(e.target.getAttribute("data-portfolio-card-index"));
              if (!Number.isNaN(idx) && !prev.has(idx)) {
                if (next === prev) next = new Set(prev);
                next.add(idx);
              }
            }
          }
          return next;
        });
      },
      {
        root: scroller,
        // Preload ~1 card-width to the left AND right of viewport.
        rootMargin: "0px 100% 0px 100%",
        threshold: 0.01,
      }
    );

    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [N]);

  // On mobile, once the src is attached, proactively call .load() on every
  // video so browsers actually download the first frame (poster). Without this,
  // iOS Safari + Chromium mobile may leave off-screen cards blank because they
  // won't preload cards outside the viewport, even with preload="auto".
  useEffect(() => {
    if (!IS_MOBILE) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const videos = scroller.querySelectorAll("video[src]");
    videos.forEach((v) => {
      try { v.load(); } catch { /* noop */ }
    });
  }, [IS_MOBILE, loadedCards]);

  const scrollByCards = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-portfolio-card]");
    if (!cards.length) return;
    const cx = getCenteredIndex();
    const nextIdx = Math.max(0, Math.min(cards.length - 1, cx + delta));
    const target = cards[nextIdx];
    if (!target) return;
    el.scrollTo({
      left: target.offsetLeft - (el.clientWidth - target.clientWidth) / 2,
      behavior: "smooth",
    });
  };

  const scrollToActive = (targetActive) => {
    // Scroll to the copy of the target-active index that is closest to current center.
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-portfolio-card]");
    if (!cards.length) return;
    const cx = getCenteredIndex();
    // Candidate positions are targetActive + k*N for k in {0,1,2}
    let best = 0;
    let bestDelta = Infinity;
    for (let k = 0; k < COPIES; k++) {
      const idx = targetActive + k * N;
      const d = Math.abs(idx - cx);
      if (d < bestDelta) {
        bestDelta = d;
        best = idx;
      }
    }
    const target = cards[best];
    if (!target) return;
    el.scrollTo({
      left: target.offsetLeft - (el.clientWidth - target.clientWidth) / 2,
      behavior: "smooth",
    });
  };

  const goPrev = () => scrollByCards(-1);
  const goNext = () => scrollByCards(+1);

  return (
    <section
      id="realisations"
      data-testid="portfolio-section"
      className="section-round bg-[#F2F6FB] px-0 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <Reveal className="mb-12 px-6 text-center sm:mb-14">
          <Overline>Réalisations</Overline>
          <h2 className="font-head mx-auto mt-4 max-w-3xl text-4xl font-extrabold text-[#0B1220] sm:text-5xl">
            Du contenu qui <span className="text-[#3B82F6]">performe</span>.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base text-[#5B6B85]">
            Faites défiler et cliquez pour activer le son.
          </p>
        </Reveal>

        {/* Carousel — infinite (3× duplicated + seamless reset) */}
        <div
          ref={scrollerRef}
          data-testid="portfolio-carousel"
          className="portfolio-scroll flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-6 pb-4 sm:gap-5 sm:px-[8vw] lg:gap-6 lg:px-[10vw]"
        >
          {displayed.map((r, i) => {
            const origIndex = i % N;
            const isHot = hovered === origIndex;
            // Copie du milieu = affichée à l'init → on préchargeait "auto" pour affichage instantané.
            const isMiddleCopy = i >= N && i < N * 2;
            return (
              <button
                key={i}
                type="button"
                data-portfolio-card
                data-portfolio-orig-index={origIndex}
                data-portfolio-card-index={i}
                onClick={() => toggleSound(origIndex)}
                onMouseEnter={() => setHovered(origIndex)}
                onMouseLeave={() => setHovered(null)}
                data-testid={`portfolio-card-${i}`}
                className="group relative aspect-[9/16] w-[68%] shrink-0 snap-center text-left sm:w-[38%] md:w-[28%] lg:w-[23%]"
              >
                <div
                  className={`absolute inset-0 overflow-hidden rounded-3xl border transition-shadow duration-300 ${
                    isHot
                      ? "border-[#2563EB]/40 shadow-[0_22px_50px_-26px_rgba(37,99,235,0.4)]"
                      : "border-[#E6EDF6] shadow-[0_10px_30px_-20px_rgba(15,23,42,0.35)]"
                  }`}
                >
                  <video
                    ref={(el) => {
                      // Register only the first copy per original index so mute/play
                      // targets one canonical <video> per client.
                      if (i < N) videoRefs.current[origIndex] = el;
                    }}
                    data-portfolio-video-index={i}
                    src={loadedCards.has(i) ? `${r.src}#t=0.5` : undefined}
                    muted
                    autoPlay
                    loop
                    playsInline
                    preload={
                      loadedCards.has(i)
                        ? // Mobile: 1 copy = up to 12 cards. Force "auto" on all
                          // so first frames are always ready — iOS/Chromium may
                          // otherwise leave cards blank until they scroll into
                          // view. On desktop we keep "auto" for the middle-copy.
                          "auto"
                        : "none"
                    }
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-transparent to-[#030712]/25" />

                  <span className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0B0B14]">
                    {r.client}
                  </span>

                  <div className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-sm">
                    <PlatformIcon platform={r.platform} />
                  </div>

                  <div className="absolute inset-0 flex items-center justify-center">
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all duration-300 group-hover:scale-110 ${
                        sound === origIndex ? "bg-[#2563EB] text-white" : "bg-white/95 text-[#0B0B14]"
                      }`}
                    >
                      {sound === origIndex ? <Volume2 size={18} /> : <VolumeX size={18} />}
                    </span>
                  </div>

                  <div
                    data-testid={`portfolio-caption-${i}`}
                    className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#030712] via-[#030712]/85 to-transparent p-4 pt-10 transition-all duration-500 ease-out ${
                      isHot ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                    }`}
                  >
                    <span
                      className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider transition-transform duration-500 ${
                        isHot ? "scale-100" : "scale-0"
                      } ${r.tag === "Ads" ? "bg-[#2563EB] text-white" : "bg-white text-[#0B0B14]"}`}
                    >
                      {r.tag}
                    </span>
                    <p className="mt-2.5 font-head text-[15px] font-bold leading-none text-white underline decoration-[#3B82F6] decoration-2 underline-offset-4">
                      {r.line1}
                    </p>
                    <p className="mt-2 text-[12px] font-medium leading-snug text-white/85">
                      {r.line2}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Dots + Arrows */}
        <div className="mt-8 flex items-center justify-between gap-4 px-6 sm:px-[8vw] lg:px-[10vw]">
          <div data-testid="portfolio-dots" className="flex items-center gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => scrollToActive(i)}
                aria-label={`Aller à la vidéo ${i + 1}`}
                data-testid={`portfolio-dot-${i}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  active === i ? "w-6 bg-[#2563EB]" : "w-2 bg-[#0B1220]/20 hover:bg-[#0B1220]/40"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              aria-label="Vidéo précédente"
              data-testid="portfolio-prev"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#0B1220]/15 bg-white text-[#0B1220] transition-all duration-300 hover:border-[#2563EB] hover:text-[#2563EB]"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Vidéo suivante"
              data-testid="portfolio-next"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-[#0B1220]/15 bg-white text-[#0B1220] transition-all duration-300 hover:border-[#2563EB] hover:text-[#2563EB]"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="mt-12 px-6 text-center">
          <span className="inline-block animate-nudge">
            <a
              href={ICLOSED_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portfolio-cta"
              className="btn-blue group relative inline-flex items-center gap-2.5 overflow-hidden rounded-full px-8 py-4 text-base font-semibold text-white"
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 -translate-x-[120%] skew-x-[-20deg] bg-white/25 blur-sm transition-transform duration-700 ease-out group-hover:translate-x-[220%]"
              />
              <span className="relative">Je veux ce type de contenu</span>
              <ChevronRight size={18} className="relative transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </span>
        </div>
      </div>
    </section>
  );
}
